import { useDispatch, useSelector } from "react-redux";
import { GetProposalConflictParams, SearchFieldProposalConflict, useGetProposalListConflictQuery } from "../../api/gms/proposal/proposal-api"
import { getErrorMessage } from "../../utils/get-error-message.utils"
import { selectAllConflictProposalPagination, selectBookedProposalPagination, setPagination } from "../../features/pagination/pagination-slice";
import { options } from "../../data/group-insurance";
import { DropdownOption } from "../../features/auth/auth-state";
import { useEffect } from "react";
import { totalPages, TotalPages } from "../../features/pagination/pagination-inital-state";
import { selectUserCredentials } from "../../features/auth/auth-slice";

export const useConflictProposal = () => {

    const userData = useSelector(selectUserCredentials);
    const dispatch = useDispatch();
    const {
        searchType,
        searchValue,
        page,
        defaultTotalPages,
        isTyping,
        isPageSizeChanging,
        isDebounceDone,
    } = useSelector(selectAllConflictProposalPagination);

    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name } : null;
    const setSelectedSearchType = ( type: { id: number; name: string } | null ) => {
        if (!type) {
            dispatch(setPagination({ type: 'conflictProposalForApproval', value: { searchType: null, searchValue: "", page: 1 }}));
            return;
        }

        const option = options.find((option) => option.id === type.id) ?? null;
        dispatch(setPagination({ type: 'conflictProposalForApproval', value: { searchType: option, searchValue: "", page: 1, }}));
    };

    const setSearchValue = (value: string) => {
            dispatch(setPagination({ type: 'conflictProposalForApproval', value: {searchValue: value}}));
    };

    const setPage = (value: number) => {
        dispatch(setPagination({type: 'conflictProposalForApproval', value: {page: value}}));
    };

    const params: GetProposalConflictParams = { page, per_page: defaultTotalPages.value, };
    if (searchType && searchValue.trim()) {
        params[searchType.value as SearchFieldProposalConflict] = searchValue.trim();
    }

    const { 
        data: getConflictProposalForApproval, 
        isLoading: isLoadingGetConflictProposalForApproval, 
        isError: isErrorGetConflictProposalForApproval, 
        error: errorGetConflictProposalForApproval, 
        refetch: refetchGetConflictProposalForApproval, 
        isFetching: isFetchingGetConflictProposalForApproval 
    } = useGetProposalListConflictQuery(params, {skip: !userData})
    const errorMessageGetConflictProposalForApproval = getErrorMessage(errorGetConflictProposalForApproval)

    /** Handle search debounce */
    useEffect(() => {
        dispatch(setPagination({type: 'conflictProposalForApproval', value: { isTyping: true,isDebounceDone: false }}));

        const timer = setTimeout(() => {
            dispatch(setPagination({type: 'conflictProposalForApproval', value: { isDebounceDone: true }}));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, dispatch]);

    /** Stop typing state after debounce and API request has finished. */
    useEffect(() => {
        if (isDebounceDone && !isFetchingGetConflictProposalForApproval) {
            dispatch(setPagination({type: 'conflictProposalForApproval', value: { isTyping: false }}));
        }
    }, [isDebounceDone, isFetchingGetConflictProposalForApproval, dispatch]);

    /** Stop page-size loading state after the API request has finished. */
    useEffect(() => {
        if (isPageSizeChanging && !isFetchingGetConflictProposalForApproval) {
            dispatch(setPagination({type: 'conflictProposalForApproval', value: { isPageSizeChanging: false }}));
        }
    }, [isPageSizeChanging, isFetchingGetConflictProposalForApproval, dispatch]);

    /** Handle page-size change */
    const handleChangePageSize = (value: TotalPages) => {
        dispatch(setPagination({
            type: 'conflictProposalForApproval',
            value: {
                defaultTotalPages: value,
                page: 1,
                isPageSizeChanging: true
            }
        }));
    };

    /** Handle reload */
    const handleReload = () => {
        dispatch(setPagination({type: 'conflictProposalForApproval', value: {page: 1}}));
        refetchGetConflictProposalForApproval();
    };

    /** Combined loading state */
    const loading = isTyping || isLoadingGetConflictProposalForApproval || isPageSizeChanging;

    const normalizedOptions: DropdownOption[] = options.map(
        (option) => ({id: option.id, name: option.name})
    );

    return {
        getConflictProposalForApproval,
        isLoadingGetConflictProposalForApproval,
        isErrorGetConflictProposalForApproval,
        errorGetConflictProposalForApproval,
        errorMessage: errorMessageGetConflictProposalForApproval,
        refetchGetConflictProposalForApproval,
        isFetchingGetConflictProposalForApproval,

        normalizedOptions,
        searchType: selectedSearchType,
        setSearchType: setSelectedSearchType,
        searchValue,
        setSearchValue,
        setPage,
        pagedData: getConflictProposalForApproval?.data ?? [],
        loading,
        handleReload,
        defaultTotalPages,
        setDefaultTotalPages: handleChangePageSize,
        proposalDefaultPageSize: totalPages,
        handleChangePageSize,
    }
}